import {
  MAGIC_INITIATE_LISTS, STANDARD_LANGUAGES, VERSATILE_FEATS, backgroundIncreaseError, isVersatileSpecies, originLanguagesError,
} from '../../../shared/rules/origins.mjs';

// Wizard-only fields for 2024 origin choices (edition audit ORG-01/03/04/08). The server validates and applies
// them; these helpers keep the wizard, the generator and the submit payload in step.
export const ORIGIN_FORM_DEFAULTS = Object.freeze({
  backgroundIncreaseMode: '2-1',
  backgroundIncrease: {},
  originLanguages: [],
  versatileFeat: '',
  versatileSkills: [],
  versatileSpellList: '',
});
const TRANSIENT = Object.keys(ORIGIN_FORM_DEFAULTS);

export const originErrors = (form = {}) => {
  const errors = {};
  if (form.background?.abilityScores) {
    const increase = backgroundIncreaseError(form.background, form.backgroundIncrease);
    if (increase) errors.backgroundIncrease = increase;
  }
  const languages = originLanguagesError(form.originLanguages);
  if (languages) errors.originLanguages = languages;
  if (isVersatileSpecies(form.race)) {
    if (!VERSATILE_FEATS.includes(form.versatileFeat)) errors.versatileFeat = 'Choose an Origin feat for Versatile.';
    else if (form.versatileFeat === 'Skilled' && new Set((form.versatileSkills || []).filter(Boolean)).size !== 3) errors.versatileFeat = 'Choose three different skills for Skilled.';
    else if (form.versatileFeat === 'Magic Initiate' && !MAGIC_INITIATE_LISTS.includes(form.versatileSpellList)) errors.versatileFeat = 'Choose a spell list for Magic Initiate.';
  }
  return errors;
};

/** Turn wizard origin fields into the create payload: background.abilityIncrease, languages and originFeats. */
export const withOriginPayload = (character = {}) => {
  const payload = { ...character };
  if (payload.background && typeof payload.background === 'object') {
    payload.background = { ...payload.background, abilityIncrease: { ...(character.backgroundIncrease || {}) } };
  }
  if (Array.isArray(character.originLanguages) && character.originLanguages.length) payload.languages = [...character.originLanguages];
  if (isVersatileSpecies(character.race) && character.versatileFeat) {
    const feat = { source: 'species', featName: character.versatileFeat };
    if (character.versatileFeat === 'Skilled') feat.skills = Object.fromEntries((character.versatileSkills || []).filter(Boolean).map((skill) => [skill, { proficient: true }]));
    if (character.versatileFeat === 'Magic Initiate') feat.spellList = character.versatileSpellList;
    payload.originFeats = [feat];
  }
  TRANSIENT.forEach((key) => { delete payload[key]; });
  return payload;
};

/** Generator defaults: +2 then +1 by class priority, two random Standard languages and Alert for a Human. */
export const generatedOriginChoices = ({ background, race, priority = [], random = Math.random }) => {
  const allowed = Array.isArray(background?.abilityScores) ? background.abilityScores : [];
  const ranked = [...allowed].sort((a, b) => {
    const rank = (ability) => { const index = priority.indexOf(ability); return index < 0 ? priority.length : index; };
    return rank(a) - rank(b);
  });
  const languages = [...STANDARD_LANGUAGES];
  const originLanguages = [0, 1].map(() => languages.splice(Math.floor(random() * languages.length), 1)[0]);
  return {
    ...ORIGIN_FORM_DEFAULTS,
    backgroundIncrease: ranked.length === 3 ? { [ranked[0]]: 2, [ranked[1]]: 1 } : {},
    originLanguages,
    ...(isVersatileSpecies(race) ? { versatileFeat: 'Alert' } : {}),
  };
};
